// public/js/hooks/useGameDiscovery.js
import { useState, useEffect, useCallback } from 'react';
import { useProgress } from '../contexts/ProgressContext';
import { useDiscovery } from '../contexts/DiscoveryContext';

export const useGameDiscovery = (settings, settingsTimestamp, setGames, onDiscoveryComplete) => {
    const { startProgress, updateProgress, completeProgress } = useProgress();
    const { eventSourceRef, setEventSourceRef, discoveryProgress, setDiscoveryProgress, isDiscoveryActive } = useDiscovery();
    
    // Use context-level progress and active state
    const [loading, setLoading] = useState(isDiscoveryActive);
    const [stats, setStats] = useState(discoveryProgress);

    // Update local state when context state changes
    useEffect(() => {
        setLoading(isDiscoveryActive);
        setStats(discoveryProgress);
    }, [isDiscoveryActive, discoveryProgress]);

    const cleanup = useCallback(() => {
        // Don't cleanup EventSource here - let context manage it
        // This prevents interruption when switching tabs
        console.log('Hook cleanup called - preserving background discovery');
    }, []);

    const handleProgressUpdate = useCallback((data) => {
        const newStats = {
            processed: data.processed || 0,
            total: data.total || 0,
            matching: data.matching || 0
        };

        // Update both local and context state for persistence across tab switches
        setStats(newStats);
        setDiscoveryProgress(newStats);

        if (data.total && data.processed) {
            updateProgress(
                data.processed,
                data.total,
                `Processed ${data.processed.toLocaleString()} of ${data.total.toLocaleString()} games`
            );
        }
    
        if (data.games && data.games.length > 0) {
            setGames(data.games);
        } else {
            console.log('No games in progress update:', {
                hasGames: !!data.games,
                gamesType: typeof data.games,
                dataKeys: Object.keys(data)
            });
        }
    
        if (data.isComplete) {
            setLoading(false);
            completeProgress();
            onDiscoveryComplete?.(); // Notify context that discovery is complete
            // Don't cleanup here - let context handle EventSource cleanup
        }
    }, [setGames, updateProgress, completeProgress, onDiscoveryComplete, setDiscoveryProgress]);

    const fetchGames = useCallback(async (currentSettings = settings) => {
        // Check if discovery is already running - if so, don't start another one
        if (eventSourceRef) {
            console.log('Discovery already running in background - not starting another');
            setLoading(true); // Show we're reconnecting to active discovery
            return;
        }

        // Clear previous games before starting new discovery
        setGames([]);
        setLoading(true);
        startProgress(0, 'Starting discovery...');

        try {
            // TEMPORARY: Use regular endpoint instead of streaming to test
            const params = new URLSearchParams({
                filters: JSON.stringify(currentSettings.filters),
                scoring: JSON.stringify(currentSettings.scoring)
            });

            console.log('Settings being sent:', {
                rawSettings: currentSettings,
                encodedFilters: JSON.stringify(currentSettings.filters),
                encodedScoring: JSON.stringify(currentSettings.scoring),
                fullParams: params.toString()
            });

            // Use V1 streaming endpoint for consistency with dashboard
            console.log(`Starting streaming discovery: /analytics/api/discovery/games/stream?${params.toString()}`);
            
            const eventSource = new EventSource(`/analytics/api/discovery/games/stream?${params.toString()}`);
            setEventSourceRef(eventSource);

            let receivedGames = [];
            const seenGameIds = new Set(); // Track game IDs to detect duplicates
            let duplicateCount = 0;
            let streamStartTime = Date.now();
            let lastProgressTime = Date.now();

            eventSource.onmessage = function(event) {
                try {
                    const data = JSON.parse(event.data);
                    console.log('Stream message received:', data.type, data);

                    switch (data.type) {
                        case 'connected':
                            console.log('Discovery stream connected:', data.message);
                            startProgress();
                            break;

                        case 'test':
                            console.log('Test message received:', data.message);
                            break;

                        case 'keepalive':
                            console.log('Keep-alive received at', data.timestamp, 'uptime:', data.uptime + 'ms');
                            lastProgressTime = Date.now();
                            break;

                        case 'progress':
                            console.log(`Progress: ${data.processed}/${data.total}, found ${data.matching} games (batch ${data.batch})`);
                            handleProgressUpdate(data);
                            lastProgressTime = Date.now();
                            break;

                        case 'game':
                            // Check for duplicates before adding
                            const gameId = data.game.gameId || data.game.name;
                            if (seenGameIds.has(gameId)) {
                                duplicateCount++;
                                console.warn(`⚠️ DATABASE DUPLICATE DETECTED: Game "${data.game.name}" (ID: ${gameId}) - This indicates a database issue that should be investigated.`);
                            } else {
                                seenGameIds.add(gameId);
                                receivedGames.push(data.game);
                                setGames([...receivedGames]);
                            }
                            break;

                        case 'complete':
                            console.log('Discovery completed:', data.summary);
                            if (duplicateCount > 0) {
                                console.warn(`⚠️ DISCOVERY SUMMARY: Found ${duplicateCount} duplicate game(s) in database. Duplicates were filtered from results.`);
                            }
                            setGames(receivedGames);
                            updateProgress(100, 100, `Found ${receivedGames.length} games`);
                            completeProgress();
                            onDiscoveryComplete?.();
                            eventSource.close();
                            setEventSourceRef(null);
                            setLoading(false);
                            break;

                        case 'error':
                            console.error('Stream error:', data.error);
                            eventSource.close();
                            setEventSourceRef(null);
                            setLoading(false);
                            completeProgress();
                            break;

                        default:
                            console.log('Unknown stream message type:', data.type);
                    }
                } catch (parseError) {
                    console.error('Error parsing stream message:', parseError, event.data);
                }
            };

            eventSource.onerror = function(error) {
                console.error('EventSource error:', error);
                const streamDuration = Date.now() - streamStartTime;
                const timeSinceLastProgress = Date.now() - lastProgressTime;
                
                console.log(`Stream failed after ${streamDuration}ms, ${timeSinceLastProgress}ms since last progress`);
                
                eventSource.close();
                setEventSourceRef(null);
                setLoading(false);
                completeProgress();
            };

            eventSource.onopen = function() {
                console.log('EventSource connection opened');
                setLoading(true);
            };

            // Fallback to V1 non-streaming if streaming fails (REMOVED - we want streaming to work)
            // The V2 streaming endpoint has proper keep-alive handling

        } catch (error) {
            console.error('Error setting up game discovery:', error);
            setEventSourceRef(null);
            setLoading(false);
            completeProgress();
        }
    }, [startProgress, handleProgressUpdate, completeProgress, eventSourceRef, setEventSourceRef]);

    useEffect(() => {
        // Only run if settingsTimestamp exists AND it's greater than 0 (meaning user triggered it)
        // Don't auto-run on initial mount or when settings change - only when explicitly triggered
        if (settingsTimestamp && settingsTimestamp > 0) {
            console.log('Starting discovery with settings:', settings);
            fetchGames(settings); // Pass current settings to avoid stale closure
        }
        // Don't cleanup on unmount - let context handle background processing
    }, [settingsTimestamp]); // Remove fetchGames dependency to prevent auto-triggering on settings changes

    return {
        loading,
        stats,
        cleanup
    };
};